/** @jsxImportSource @emotion/react */
import { StylesClassName, UseStyles } from 'types/styles';
import { Box, Theme, useTheme } from '@mui/material';
import { InputHTMLAttributes, ReactNode } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleTwoToneIcon from '@mui/icons-material/CircleTwoTone';

interface CheckPaneItemProps {
  className?: StylesClassName;
  checkboxProps?: InputHTMLAttributes<HTMLInputElement>;
  children?: ReactNode;
  disabled?: boolean;
}

const CheckPaneItem: React.FC<CheckPaneItemProps> = ({
  className,
  checkboxProps,
  children,
  disabled = false,
  ...rest
}) => {
  const theme = useTheme() as Theme;
  const classes = useStyles(theme);

  return (
    <Box component="label" css={[classes.CheckPaneItem, className, disabled && classes.disabled]} {...rest}>
      <input type="checkbox" hidden css={classes.input} {...checkboxProps} />
      <CheckCircleIcon css={classes.check} className="checked-icon" />
      <CircleTwoToneIcon css={classes.uncheck} className="unchecked-icon" />
      <Box css={classes.content}>{children}</Box>
    </Box>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  CheckPaneItem: {
    display: 'flex',
    alignItems: 'flex-start',
    background: theme.palette.common.white,
    borderRadius: 8,
    padding: theme.spacing(4),
    gap: theme.spacing(4),
  },
  disabled: {
    pointerEvents: 'none',
    color: theme.palette.custom.primary.grey[100],

    '& .checked-icon, & .unchecked-icon': {
      color: theme.palette.custom.primary.grey[80],
    },
  },
  check: {
    color: theme.palette.custom.secondary.green[100],
    fontSize: 24,
    display: 'none',
  },
  uncheck: {
    color: theme.palette.custom.primary.grey[100],
    fontSize: 24,
    display: 'block',
  },
  input: {
    '&:checked': {
      '& ~ .checked-icon': {
        display: 'block',
      },
      '& ~ .unchecked-icon': {
        display: 'none',
      },
    },
  },
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    gap: theme.spacing(2),

    '& p': {
      margin: 0,
    },
  },
});

export default CheckPaneItem;
